'use client';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { genericMemo } from '@/types/use';
import { useTranslations } from 'next-intl';
import CustomLoading from '../custom-loading';

export type OptionPrimitive<T extends {} = any> = {
    label: string;
    value: string;
    _origin?: T;
};

type SelectPrimitiveProps<T extends {}> = {
    options: OptionPrimitive[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    isLoading?: boolean;

    value?: string;
    onChange?: (val?: string, option?: OptionPrimitive<T>) => void;
};

const SelectPrimitive = <T extends {} = any>({
    options,
    placeholder,
    onChange,
    value,
    disabled,
    className,
    isLoading,
}: SelectPrimitiveProps<T>) => {
    const transDef = useTranslations('System');

    const onValueChange = (selectVal: string) => {
        if (selectVal === value) {
            onChange?.('', undefined);
            return;
        }

        const selectedOption = options.find((x) => x.value === selectVal);
        onChange?.(selectVal, selectedOption);
    };

    return (
        <Select value={value} onValueChange={onValueChange} disabled={disabled}>
            <SelectTrigger
                className={cn(
                    'w-full transition-colors duration-200',
                    className
                )}
            >
                <SelectValue placeholder={placeholder ?? `Chọn...`} />
            </SelectTrigger>
            <SelectContent aria-modal={false}>
                {isLoading ? (
                    <div className='flex min-h-32 items-center justify-center'>
                        <CustomLoading.Round size='small' />
                    </div>
                ) : options.length ? (
                    options.map((x) => (
                        <SelectItem
                            value={x.value}
                            key={x.value}
                            className='cursor-pointer'
                        >
                            {x.label}
                        </SelectItem>
                    ))
                ) : (
                    <div className='flex items-center justify-center py-4 text-sm'>
                        <span>{transDef('System_NoData')}</span>
                    </div>
                )}
            </SelectContent>
        </Select>
    );
};

export default genericMemo(SelectPrimitive);
